import type { LucideIcon } from "lucide-react";
import { Store, Truck, Users, Headset } from "lucide-react";

export interface ContactChannel {
  id: "atacado" | "fornecedor" | "rh" | "sac";
  title: string;
  description: string;
  phone: string;
  /** Mensagem base enviada ao WhatsApp (texto puro, sem encoding). */
  message: string;
  requiresCnpj: boolean;
  icon: LucideIcon;
}

export const contactChannels: ContactChannel[] = [
  {
    id: "atacado",
    title: "Atacado",
    description: "Compre em maior quantidade",
    phone: "553138781401",
    message:
      "Olá, vim através do site da Toraflex Colchões e gostaria de falar com o setor de Vendas.",
    requiresCnpj: true,
    icon: Store,
  },
  {
    id: "fornecedor",
    title: "Quero ser fornecedor",
    description: "Quero fornecer produtos ou serviços",
    phone: "553138781420",
    message:
      "Olá, vim através do site da Toraflex Colchões e gostaria de falar com o setor de Compras.",
    requiresCnpj: true,
    icon: Truck,
  },
  {
    id: "rh",
    title: "Recursos Humanos",
    description: "Vagas e assuntos relacionados ao RH",
    phone: "553138781409",
    message:
      "Olá, vim através do Site da Toraflex Colchões e gostaria de falar no setor de RH",
    requiresCnpj: false,
    icon: Users,
  },
  {
    id: "sac",
    title: "SAC",
    description: "Atendimento ao consumidor",
    phone: "553138781402",
    message:
      "Olá, vim através do Site da Toraflex Colchões e gostaria de falar no setor de Serviço de Atendimento ao Consumidor",
    requiresCnpj: false,
    icon: Headset,
  },
];

/**
 * Regra comercial centralizada: mensagem exibida quando o usuário
 * declara não possuir CNPJ. Alterar aqui muda o comportamento por canal.
 */
export const noCnpjCopy: Record<
  ContactChannel["id"],
  { title: string; text: string } | null
> = {
  atacado: {
    title: "Este atendimento é exclusivo para empresas",
    text: "Para compras no atacado, atendemos exclusivamente pessoas jurídicas com CNPJ válido. Se você precisa de outro tipo de atendimento, volte e escolha uma das opções disponíveis.",
  },
  fornecedor: {
    title: "Este atendimento é exclusivo para empresas",
    text: "O cadastro de fornecedores é feito somente com pessoas jurídicas com CNPJ válido. Se você precisa de outro tipo de atendimento, volte e escolha uma das opções disponíveis.",
  },
  rh: null,
  sac: null,
};

export function buildWhatsAppUrl(
  channel: ContactChannel,
  company?: {
    cnpj: string;
    legalName: string;
    tradeName?: string;
    city?: string;
    state?: string;
  },
) {
  let text = channel.message;
  if (company) {
    text += `\n\n*CNPJ informado:* ${formatCnpj(company.cnpj)}`;
    if (company.legalName) text += `\n*Empresa:* ${company.legalName}`;
    if (company.tradeName) text += `\n*Nome fantasia:* ${company.tradeName}`;
    const location = [company.city, company.state].filter(Boolean).join(" - ");
    if (location) text += `\n*Cidade/Estado:* ${location}`;
  }
  return `https://api.whatsapp.com/send?phone=${channel.phone}&text=${encodeURIComponent(text)}`;
}

export function formatCnpj(value: string) {
  const d = value.replace(/\D/g, "").slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
}

export function isValidCnpj(value: string): boolean {
  const digits = value.replace(/\D/g, "");
  if (digits.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(digits)) return false;
  const calc = (len: number) => {
    let sum = 0;
    let pos = len - 7;
    for (let i = 0; i < len; i++) {
      sum += parseInt(digits[i], 10) * pos--;
      if (pos < 2) pos = 9;
    }
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
  };
  return calc(12) === parseInt(digits[12], 10) && calc(13) === parseInt(digits[13], 10);
}
